import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { peek, storedLocation } from '../data/routeData';
import { calendarKey, loadCalendar } from '../data/routeLoaders';
import { Navbar } from '../components';
import LoadingSpinner from '../components/LoadingSpinner';
import RouteLoadError from '../components/transitions/RouteLoadError';
import { useUser, useAuth } from '../context';
import { realPlace } from '../utils/placeLabel';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import { PageHero, StatPill, Pill, Btn, RingMark } from '../components/pagesystem/PageSystem';
import '../components/calendar/calendar.css';

/**
 * /calendar — WHAT'S ON. (handoff: design_handoff_guest_start_calendar/Calendar.dc.html).
 * MONTH · WEEK · DAY over the same `loadCalendar(cursor, …, {lat,lng})` read, `byDay`
 * bucketing filtered by the MINE / TOWN scope, prev/next stepping by the active unit. The
 * grid draws event chips on desktop and species dots on phones; the day agenda on the right
 * carries one card per item with its own action.
 */
const TYPE_META = {
  signal: { color: '#00FFFF', label: 'PORTAL' },
  party_starter: { color: '#FF00FF', label: 'PARTY STARTER' },
  guaranteed: { color: '#000', label: 'GUARANTEED' },
};
const SPECIES_ORDER = ['signal', 'party_starter', 'guaranteed'];

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
// Zero-padded, 1-indexed yyyy-MM-dd for /events/all?day= (dayKey is for grouping only).
export const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
const up = (s) => (s || '').toUpperCase();
const money = (cents) => `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cityOf = (label) => (label ? label.split(',')[0].trim() : '');

export default function CalendarPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();
  const loc = storedLocation();
  const phone = useMediaQuery('(max-width: 639px)');
  const mobile = useMediaQuery('(max-width: 979px)');
  const topH = useTopBarHeight();

  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [items, setItems] = useState(() => peek(calendarKey(cursor)) || []);
  const [loading, setLoading] = useState(() => !peek(calendarKey(cursor)));
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(() => new Date());
  const [view, setView] = useState('month'); // 'month' | 'week' | 'day'
  const [scope, setScope] = useState('town'); // 'town' (everything near you) | 'mine' (you're in)

  const load = useCallback(async () => {
    setLoading(!peek(calendarKey(cursor)));
    try { setLoadError(null); setItems(await loadCalendar(cursor, undefined, { lat: loc?.lat, lng: loc?.lng }) || []); }
    catch (error) { setLoadError(error.message); }
    finally { setLoading(false); }
  }, [cursor, loc?.lat, loc?.lng]);
  useEffect(() => { load(); }, [load]);

  // Bucket by local day, filtered by the scope, sorted by time within the day.
  const byDay = useMemo(() => {
    const m = {};
    for (const it of items) {
      if (scope === 'mine' && !it.isMine) continue;
      const k = dayKey(new Date(it.dateTimeUtc));
      (m[k] ||= []).push(it);
    }
    Object.values(m).forEach((l) => l.sort((a, b) => new Date(a.dateTimeUtc) - new Date(b.dateTimeUtc)));
    return m;
  }, [items, scope]);

  // The month grid: weeks of 7, Sunday first, a trailing all-next-month week trimmed.
  const weeks = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const rows = [];
    for (let r = 0; r < 6; r++) {
      const row = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + r * 7 + i); return d; });
      if (r > 3 && !row.some((d) => d.getMonth() === cursor.getMonth())) continue;
      rows.push(row);
    }
    return rows;
  }, [cursor]);

  // The Sunday-first week holding the selected day (WEEK view).
  const weekRow = useMemo(() => {
    const start = new Date(selected); start.setDate(selected.getDate() - selected.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [selected]);
  const gridRows = view === 'week' ? [weekRow] : weeks;

  const today = new Date();
  const period = view === 'month'
    ? up(cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }))
    : view === 'week'
      ? `WEEK OF ${up(weekRow[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}`
      : up(selected.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));

  const selectDay = (d) => { setSelected(new Date(d)); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); };
  // Prev/next steps by the active unit.
  const step = (dir) => {
    if (view === 'month') { setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1)); return; }
    const d = new Date(selected); d.setDate(selected.getDate() + dir * (view === 'week' ? 7 : 1));
    selectDay(d);
  };

  const selectedItems = byDay[dayKey(selected)] || [];
  const monthItems = items.filter((it) => { const d = new Date(it.dateTimeUtc); return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear(); });
  const city = cityOf(realPlace(loc?.label, profile?.location));
  const isToday = dayKey(selected) === dayKey(today);
  const selPast = !isToday && selected < today;
  const isDay = view === 'day';

  const openItem = (it) => navigate(it.type === 'signal' ? `/portals/${it.id}` : `/events/${it.inviteCode || it.id}`);
  const act = (it) => {
    if (it.type === 'guaranteed' && it.isMine) { navigate('/my-tickets'); return; }
    openItem(it);
  };
  const actLabel = (it) => {
    if (it.type === 'signal') return it.isMine ? "YOU'RE IN ✓" : "I'M IN →";
    if (it.type === 'party_starter') return it.isMine ? 'PLEDGED ✓' : 'PLEDGE →';
    if (it.isMine) return 'MY TICKET →';
    const price = it.displayPriceCents ?? it.ticketPriceCents;
    return price > 0 ? `GET IN · ${money(price)}` : 'RSVP · FREE';
  };
  const metaOf = (it) => {
    const place = up(it.venueName || it.locationCity || '');
    if (it.type === 'signal') {
      return [up(it.hostName), it.responseCount ? `${it.responseCount} IN` : '', it.expiresAtUtc ? `CLOSES ${fmtTime(it.expiresAtUtc)}` : ''].filter(Boolean).join(' · ');
    }
    if (it.type === 'party_starter') return place;
    const price = it.displayPriceCents ?? it.ticketPriceCents;
    return [place, price > 0 ? money(price) : 'FREE'].filter(Boolean).join(' · ');
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const maxChips = view === 'week' ? 6 : 2;

  return (
    <div className="ps-page" style={{ '--ps-top': `${topH}px`, '--cl-rail-top': `${topH + 24}px` }}>
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <PageHero
        kicker={`CALENDAR · ${city ? city.toUpperCase() : 'NEAR YOU'} · 50 MI`}
        title="WHAT'S ON."
        sub="The event calendar around you."
        right={(
          <div className="ps-stats" role="tablist" aria-label="Scope">
            <StatPill n={monthItems.filter((i) => i.isMine).length} label="MINE" on={scope === 'mine'} onClick={() => setScope('mine')} testId="scope-mine" />
            <StatPill n={monthItems.length} label="TOWN" on={scope === 'town'} onClick={() => setScope('town')} testId="scope-town" />
          </div>
        )}
      >
        <div className="cl-controls">
          <div className="ps-pills" role="tablist" aria-label="View">
            {['month', 'week', 'day'].map((v) => (
              <Pill key={v} role="tab" tall grey on={view === v} ariaSelected={view === v} onClick={() => setView(v)}>{v.toUpperCase()}</Pill>
            ))}
          </div>
          <i />
          <div className="cl-nav">
            <button type="button" className="cl-arrow" aria-label="Previous" onClick={() => step(-1)}>‹</button>
            <span className="cl-period" data-testid="period">{period}</span>
            <button type="button" className="cl-arrow" aria-label="Next" onClick={() => step(1)}>›</button>
            <button type="button" className="cl-today" onClick={() => selectDay(today)}>TODAY</button>
          </div>
        </div>
      </PageHero>

      <main className="ps-container ps-main cl-main">
        <RouteLoadError error={loadError} onRetry={load} />
        <div className="cl-legend" aria-label="Legend">
          {SPECIES_ORDER.map((t) => <span key={t}><i style={{ background: TYPE_META[t].color }} />{TYPE_META[t].label}</span>)}
        </div>

        <div className="cl-grid" data-day={isDay ? '' : undefined}>
          {!isDay && (
            <div className="cl-cal" data-week={view === 'week' ? '' : undefined}>
              <div className="cl-dow">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span key={i} data-weekend={i === 0 || i === 6 ? '' : undefined}>{d}</span>)}
              </div>
              {loading ? (
                <div className="cl-loading"><LoadingSpinner label="Loading the calendar…" /></div>
              ) : gridRows.map((row, ri) => (
                <div key={ri} className="cl-week">
                  {row.map((d) => {
                    const inMonth = view === 'week' || d.getMonth() === cursor.getMonth();
                    const isSel = dayKey(d) === dayKey(selected);
                    const isTod = dayKey(d) === dayKey(today);
                    const list = inMonth ? (byDay[dayKey(d)] || []) : [];
                    const more = list.length - maxChips;
                    return (
                      <button
                        key={dayKey(d)}
                        type="button"
                        className="cl-cell"
                        disabled={!inMonth}
                        data-out={inMonth ? undefined : ''}
                        data-sel={isSel && inMonth ? '' : undefined}
                        data-today={isTod && inMonth ? '' : undefined}
                        onClick={() => inMonth && selectDay(d)}
                        aria-label={d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                        aria-pressed={isSel && inMonth}
                      >
                        <span className="cl-cell-top">
                          <span className="cl-num">{d.getDate()}</span>
                          {!phone && more > 0 && <span className="cl-more">+{more}</span>}
                        </span>
                        {phone ? (
                          list.length > 0 && (
                            <span className="cl-dots">{list.map((e, i) => <i key={i} style={{ background: TYPE_META[e.type]?.color || '#000' }} />)}</span>
                          )
                        ) : (
                          list.slice(0, maxChips).map((e) => (
                            <span key={`${e.type}-${e.id}`} className="cl-chip">
                              <i style={{ background: TYPE_META[e.type]?.color || '#000' }} />
                              <span>{view === 'week' ? `${fmtTime(e.dateTimeUtc)} ${e.title}` : e.title}</span>
                            </span>
                          ))
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <aside className="cl-aside" data-static={mobile || isDay ? '' : undefined} aria-label="Day agenda">
            <div className="cl-aside-head">
              <span className="cl-aside-title">
                <small data-today={isToday ? '' : undefined}>{isToday ? 'TODAY' : selPast ? 'PAST' : 'COMING UP'}</small>
                <b>{up(selected.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }))}</b>
              </span>
              {isDay ? (
                <span className="cl-aside-nav">
                  <button type="button" aria-label="Previous day" onClick={() => step(-1)}>‹</button>
                  <button type="button" aria-label="Next day" onClick={() => step(1)}>›</button>
                </span>
              ) : (
                <span className="cl-aside-count">{selectedItems.length} {selectedItems.length === 1 ? 'EVENT' : 'EVENTS'}</span>
              )}
            </div>

            {selectedItems.map((it) => {
              const meta = TYPE_META[it.type] || TYPE_META.guaranteed;
              const isPortal = it.type === 'signal';
              const isStarter = it.type === 'party_starter';
              const pct = isStarter && it.floorGoal > 0 ? Math.min(100, Math.round((it.floorCurrent / it.floorGoal) * 100)) : 0;
              return (
                <div key={`${it.type}-${it.id}`} className="cl-item" data-portal={isPortal ? '' : undefined} data-testid="agenda-item">
                  <div className="cl-item-top">
                    {isPortal ? <RingMark size={36} ripple glyph={14} /> : it.posterUrl && <img src={it.posterUrl} alt="" />}
                    <span className="cl-item-tags">
                      <span className="cl-species" style={{ background: meta.color, color: isPortal ? '#000' : '#fff' }}>{meta.label}</span>
                      <span className="cl-item-time">{fmtTime(it.dateTimeUtc)}</span>
                      {it.isMine && <span className="cl-mine">· YOU'RE IN</span>}
                    </span>
                  </div>
                  <button type="button" className="cl-item-title" onClick={() => openItem(it)}>{it.title}</button>
                  {metaOf(it) && <span className="cl-item-meta">{metaOf(it)}</span>}
                  {isStarter && it.floorGoal > 0 && (
                    <span className="cl-floor">
                      <span className="cl-track"><i style={{ width: `${pct}%` }} /></span>
                      <span>{it.floorCurrent} / {it.floorGoal} TO GREENLIGHT</span>
                    </span>
                  )}
                  <span className="cl-item-act">
                    <Btn
                      white={isPortal && !it.isMine}
                      cyan={isPortal && it.isMine}
                      grey={!isPortal && it.isMine}
                      onClick={() => act(it)}
                      style={!isPortal && it.isMine ? { background: '#fff' } : undefined}
                    >
                      {actLabel(it)}
                    </Btn>
                  </span>
                </div>
              );
            })}

            {selectedItems.length === 0 && !loading && (
              <div className="cl-empty">
                <b>NOTHING ON THIS DAY.</b>
                <span>Pick another, or start something.</span>
                <button type="button" className="cl-open" onClick={() => navigate('/start')}>
                  <RingMark size={32} glyph={13} />
                  OPEN A PORTAL
                </button>
              </div>
            )}

            <button type="button" className="cl-all" onClick={() => navigate(`/events/all?day=${isoDay(selected)}`)}>
              SEE ALL FOR THIS DAY →
            </button>
          </aside>
        </div>
      </main>
    </div>
  );
}
